import * as React from 'react';
import { EmbeddedTabs, EmbeddedTabList, EmbeddedTab, EmbeddedTabPanel } from '../../../shared/components/navigation/EmbeddedTabs';
import Applicants from './Applicants';
import ApplicantInvitations from './ApplicantInvitations';
import HelpIconWithTooltip from '../../../shared/components/feedback/HelpIconWithTooltip';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import { selections } from '../../applications/components/ApplicationStatus';
import type { ApplicantRecord, ProfileNote } from '../../../shared/types/domainModels';
import { fetchProfileNotes } from '../../notes/utils/fetchProfileNotes';

// React.useContext
export const ApplicantsContext = React.createContext<{
  hideProfile: (applicant: ApplicantRecord) => void;
  unhideProfile: (applicant: ApplicantRecord) => void;
  updateStatus: (applicant: ApplicantRecord, status: string) => void;
}>({
  hideProfile: () => { },
  unhideProfile: () => { },
  updateStatus: () => { }
});

interface Job {
  id: number;
  position_type: string;
  [key: string]: unknown;
}

interface ManageApplicantsProps {
  less_qualified_applicants: ApplicantRecord[];
  applicants: ApplicantRecord[];
  invited: ApplicantRecord[];
  favorites: number[];
  authenticity_token: string;
  job_type_title: string;
  job_link: string;
  position_type_name: string;
  position_type: string;
  zip: string;
  timezone: string;
  js_applicants: number;
  profile_notes: ProfileNote[];
  essential: boolean;
  hidden_applicants: ApplicantRecord[];
  job?: Job;
  requested: number[];
  accepted: number[];
  declined: number[];
}

// Custom hook for handling click outside and keyboard navigation
const useClickOutsideAndKeyboard = (
  ref: React.RefObject<HTMLDivElement>,
  onClose: () => void,
  onSelect: (index: number) => void,
  suggestions: string[],
  currentIndex: number,
  setCurrentIndex: (index: number) => void
) => {
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!suggestions.length) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setCurrentIndex(Math.min(currentIndex + 1, suggestions.length - 1));
          break;
        case "ArrowUp":
          event.preventDefault();
          setCurrentIndex(Math.max(0, currentIndex - 1));
          break;
        case "Enter":
          event.preventDefault();
          if (currentIndex >= 0) {
            onSelect(currentIndex);
          }
          break;
        case "Escape":
          event.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [ref, onClose, onSelect, suggestions, currentIndex, setCurrentIndex]);
};

const DROPDOWN_STYLES = {
  container: {
    position: "absolute" as const,
    width: "100%",
    zIndex: 1000,
    backgroundColor: "#fafafa",
    border: "1px solid #dee2e6",
    borderRadius: "0.25rem",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    marginTop: "2px",
    maxHeight: "200px",
    overflowY: "auto" as const
  },
  item: (isSelected: boolean) => ({
    cursor: "pointer",
    backgroundColor: isSelected ? "#176fc9" : "transparent",
    color: isSelected ? "white" : "inherit",
    transition: "all 0.15s ease"
  })
};

interface FilterDropdownProps {
  items: string[];
  value: string;
  onChange: (value: string) => void;
  getDisplayValue: (item: string) => string;
  placeholder: string;
  show: boolean;
  onClose: () => void;
  selectedIndex: number;
  onIndexChange: (index: number) => void;
}

function FilterDropdown({
  items,
  value,
  onChange,
  getDisplayValue,
  placeholder,
  show,
  onClose,
  selectedIndex,
  onIndexChange
}: FilterDropdownProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  useClickOutsideAndKeyboard(
    ref,
    onClose,
    (index) => {
      onChange(getDisplayValue(items[index]));
      onClose();
    },
    items.map(getDisplayValue),
    selectedIndex,
    onIndexChange
  );

  return (
    <div style={{ width: "250px", position: "relative", marginBottom: 0 }} ref={ref}>
      <Form.Group style={{ marginBottom: 0 }}>
        <div className="position-relative">
          <Form.Control
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="browsing-input-forms"
            style={{
              marginBottom: 0,
              borderRadius: '0.5rem',
              border: '1px solid #e8ecef',
              padding: '0.5rem 0.75rem',
              fontSize: '0.95rem',
              transition: 'all 0.3s ease'
            }}
          />
          {value && (
            <div
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                zIndex: 5,
                color: '#6c757d',
                transition: 'color 0.2s ease'
              }}
              onClick={() => onChange("")}
              onMouseEnter={(e) => e.currentTarget.style.color = '#085ec8'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
            >
              <i className="fa fa-times" aria-hidden="true"></i>
            </div>
          )}
        </div>
        {items.length > 0 && value && show && (
          <div style={{
            ...DROPDOWN_STYLES.container,
            borderRadius: '0.5rem',
            border: '1px solid #e8ecef',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {items.map((item, index) => (
              <div
                key={index}
                onClick={() => {
                  onChange(getDisplayValue(item));
                  onClose();
                }}
                onMouseEnter={() => onIndexChange(index)}
                onMouseLeave={() => onIndexChange(-1)}
                className="px-3 py-2 dropdown-item"
                style={{
                  ...DROPDOWN_STYLES.item(index === selectedIndex),
                  borderRadius: index === 0 ? '0.5rem 0.5rem 0 0' : index === items.length - 1 ? '0 0 0.5rem 0.5rem' : '0'
                }}
              >
                {getDisplayValue(item)}
              </div>
            ))}
          </div>
        )}
      </Form.Group>
    </div>
  );
}

interface Filters {
  locationFilter: string;
  nameFilter: string;
  dateFilter: string;
  statusFilter: string;
  locationSuggestions: string[];
  nameSuggestions: string[];
  allLocations: string[];
  allNames: string[];
}

interface FilterBarProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}

function FilterBar({
  filters,
  onFiltersChange
}: FilterBarProps) {
  const [showLocationDropdown, setShowLocationDropdown] = React.useState(false);
  const [showNameDropdown, setShowNameDropdown] = React.useState(false);
  const [locationIndex, setLocationIndex] = React.useState(-1);
  const [nameIndex, setNameIndex] = React.useState(-1);

  const handleLocationFilter = (value: string) => {
    onFiltersChange({
      ...filters,
      locationFilter: value,
      locationSuggestions: value ? filters.allLocations
        .filter(loc => loc.toLowerCase().includes(value.toLowerCase()))
        : []
    });
    if (value) {
      setShowLocationDropdown(true);
    }
  };

  const handleNameFilter = (value: string) => {
    onFiltersChange({
      ...filters,
      nameFilter: value,
      nameSuggestions: value ? filters.allNames
        .filter(name => name.toLowerCase().includes(value.toLowerCase()))
        : []
    });
    if (value) {
      setShowNameDropdown(true);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      dateFilter: e.target.value
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      ...filters,
      locationFilter: "",
      nameFilter: "",
      dateFilter: "",
      statusFilter: "",
      locationSuggestions: [],
      nameSuggestions: [],
      allLocations: filters.allLocations,
      allNames: filters.allNames,
    });
  };

  // Check if any filters are active
  const hasActiveFilters = filters.locationFilter || filters.nameFilter || filters.dateFilter || filters.statusFilter;

  return (
    <div style={{
      background: 'white',
      border: '1px solid #e8ecef',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      marginBottom: '1.5rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
    }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '1rem' }}>
        <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#034ba6', fontWeight: 600 }}>
            <i className="fa fa-filter"></i>
            Filters:
          </div>
          <FilterDropdown
            items={filters.nameSuggestions}
            value={filters.nameFilter}
            onChange={handleNameFilter}
            getDisplayValue={(name) => name}
            placeholder="Filter by Name"
            show={showNameDropdown}
            onClose={() => setShowNameDropdown(false)}
            selectedIndex={nameIndex}
            onIndexChange={setNameIndex}
          />
          <div style={{ width: "180px", position: "relative", marginBottom: 0 }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <div className="position-relative">
                <Form.Control
                  type="date"
                  value={filters.dateFilter}
                  onChange={handleDateChange}
                  className="browsing-input-forms"
                  style={{
                    marginBottom: 0,
                    borderRadius: '0.5rem',
                    border: '1px solid #e8ecef',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.95rem'
                  }}
                  placeholder="Applied Date"
                />
              </div>
            </Form.Group>
          </div>
          <div style={{ width: "180px", position: "relative", marginBottom: 0 }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <div className="position-relative">
                <Form.Select
                  value={filters.statusFilter}
                  onChange={(e) => onFiltersChange({ ...filters, statusFilter: e.target.value })}
                  className="browsing-input-forms"
                  style={{
                    marginBottom: 0,
                    borderRadius: '0.5rem',
                    border: '1px solid #e8ecef',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.95rem'
                  }}
                >
                  <option value="">All Statuses</option>
                  {selections.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Form.Select>
              </div>
            </Form.Group>
          </div>
        </div>
        <button
          onClick={clearAllFilters}
          disabled={!hasActiveFilters}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            fontWeight: 600,
            fontSize: '0.95rem',
            transition: 'all 0.3s ease',
            border: hasActiveFilters ? '2px solid #085ec8' : '1px solid #e8ecef',
            cursor: hasActiveFilters ? 'pointer' : 'not-allowed',
            background: hasActiveFilters ? 'linear-gradient(135deg, #085ec8, #176fc9)' : 'white',
            color: hasActiveFilters ? 'white' : '#6c757d',
            opacity: hasActiveFilters ? 1 : 0.6,
            boxShadow: hasActiveFilters ? '0 4px 12px rgba(8, 94, 200, 0.3)' : 'none'
          }}
        >
          <i className="fa fa-times"></i> Clear Filters
        </button>
      </div>
    </div>
  );
}

const ManageApplicants = (props: ManageApplicantsProps) => {
  const {
    less_qualified_applicants,
    applicants,
    invited,
    favorites,
    authenticity_token,
    job_type_title,
    job_link,
    position_type_name,
    position_type,
    zip,
    timezone,
    js_applicants,
    profile_notes,
    essential,
    hidden_applicants,
    job,
    requested,
    accepted,
    declined
  } = props;

  // These arrive as server-rendered props (react-on-rails), so despite the
  // ApplicantRecord[] types they can be non-array at runtime (e.g. nil from the
  // controller). Normalize to arrays so the downstream .filter/.map/.includes
  // never throw "x.filter is not a function" (Sentry DIRECTDENTAL-28C).
  const asArray = <T,>(value: T[] | null | undefined): T[] =>
    (Array.isArray(value) ? value : []);

  const [state, setState] = React.useState({
    less_qualified_applicants: asArray(less_qualified_applicants),
    applicants: asArray(applicants),
    invited: asArray(invited),
    hidden_applicants: asArray(hidden_applicants),
    notes: asArray(profile_notes),
    loaded: true,
  });

  // Applicant emails link to /jobs/:id/applicants?application_id=<id>
  // (DDSJobs#1650): open the tab that holds that application.
  const linkedApplicationId = Number(new URLSearchParams(window.location.search).get('application_id')) || null;

  const linkedApplicationTab = () => {
    if (!linkedApplicationId) return null;
    const holds = (list: ApplicantRecord[]) => list.some(applicant => applicant.id === linkedApplicationId);
    if (holds(state.hidden_applicants)) return 2;
    if (holds(state.less_qualified_applicants)) return 1;
    if (holds(state.applicants)) return 0;
    return null;
  }

  const index = () => {
    const linkedTab = linkedApplicationTab();
    if (linkedTab !== null) return linkedTab;

    // Switch statement version
    switch (window.location.hash.substr(1)) {
      case 'applicants':
        return 0;
      case 'less_qualified':
        return 1;
      case 'deleted':
        return 2;
      default:
        return 0;
    }
  }

  const [selectedTab, setSelectedTab] = React.useState(index || 0);

  // Filter state
  const allLocations = [...new Set([...state.applicants, ...state.less_qualified_applicants].map(a => `${a.profile.city}, ${a.profile.state}`))];
  const allNames = [...new Set([...state.applicants, ...state.less_qualified_applicants].map(a => a.profile.fullname))];

  const [filters, setFilters] = React.useState<Filters>({
    locationFilter: "",
    nameFilter: "",
    dateFilter: "",
    statusFilter: "",
    locationSuggestions: [],
    nameSuggestions: [],
    allLocations,
    allNames
  });

  const getNotes = () => fetchProfileNotes({
    // Functional update: the mount-time fetch must not overwrite status
    // changes made while it was in flight.
    callback: notes => setState(prev => ({ ...prev, notes, loaded: true }))
  });

  React.useEffect(() => getNotes(), []);

  const updateStatus = (applicant: ApplicantRecord, status: string) => {
    // Update applicant.status in both applicants arrays in a single state update
    const updatedApplicants = state.applicants.map(a => {
      if (a.id === applicant.id) {
        return { ...a, status: status };
      }
      return a;
    });

    const updatedLessQualifiedApplicants = state.less_qualified_applicants.map(a => {
      if (a.id === applicant.id) {
        return { ...a, status: status };
      }
      return a;
    });

    setState({
      ...state,
      applicants: updatedApplicants,
      less_qualified_applicants: updatedLessQualifiedApplicants
    });
  };

  // Filter function
  const applyFilters = (applicants: ApplicantRecord[]) => {
    return applicants.filter(applicant => {
      const locationMatch = !filters.locationFilter ||
        `${applicant.profile.city}, ${applicant.profile.state}`.toLowerCase().includes(filters.locationFilter.toLowerCase());
      const nameMatch = !filters.nameFilter ||
        applicant.profile.fullname.toLowerCase().includes(filters.nameFilter.toLowerCase());
      const dateMatch = !filters.dateFilter ||
        moment(applicant.created_at).format('YYYY-MM-DD') === filters.dateFilter;
      const statusMatch = !filters.statusFilter || applicant.status === filters.statusFilter;

      return locationMatch && nameMatch && dateMatch && statusMatch;
    });
  };

  // Reject any hidden_applicants from applicants
  const visible_applicants = state.applicants.filter(applicant => !state.hidden_applicants.includes(applicant));

  // Reject any hidden_applicants from less_qualified_applicants
  const visible_less_qualified_applicants = state.less_qualified_applicants.filter(applicant => !state.hidden_applicants.includes(applicant));

  // Apply filters
  const filtered_applicants = applyFilters(visible_applicants);
  const filtered_less_qualified_applicants = applyFilters(visible_less_qualified_applicants);

  const hideProfile = (applicant: ApplicantRecord) => {
    // Alert confirm yes or no
    if (!confirm(`Are you sure you want to hide this applicant?`)) return;

    fetch(`/job_applications/${applicant.id}/hide.json`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': authenticity_token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ hidden: !applicant.hidden })
    }).then(response => {
      setState(state => ({
        ...state,
        hidden_applicants: [
          ...state.hidden_applicants,
          applicant
        ]
      }))
      return response;
    })
  }

  const unhideProfile = (applicant: ApplicantRecord) => {
    // Alert confirm yes or no
    if (!confirm(`Are you sure you want to restore this applicant?`)) return;

    fetch(`/job_applications/${applicant.id}/unhide.json`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': authenticity_token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ hidden: false })
    }).then(response => {
      setState(state => ({
        ...state,
        hidden_applicants: state.hidden_applicants.filter(app => app.id !== applicant.id)
      }))
      return response;
    })
  }

  React.useEffect(() => {
    console.log(selectedTab);
    let hashValue;
    switch (selectedTab) {
      case 0:
        hashValue = 'applicants';
        break;
      case 1:
        hashValue = 'less_qualified';
        break;
      case 2:
        hashValue = 'deleted';
        break;
      default:
        hashValue = 'applicants';
    }
    history.replaceState(null, null, '#' + hashValue);
  }, [selectedTab]);

  // Scroll to and briefly highlight the application the email linked to.
  React.useEffect(() => {
    if (linkedApplicationTab() === null) return;

    const timer = window.setTimeout(() => {
      const card = document.getElementById(`application-${linkedApplicationId}`);
      if (!card) return;
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      card.classList.add('profile-list-modern--linked');
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  // Listen for hash changes from external navigation (e.g., main tab clicks)
  React.useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      let newTab;
      switch (hash) {
        case 'applicants':
          newTab = 0;
          break;
        case 'less_qualified':
          newTab = 1;
          break;
        case 'deleted':
          newTab = 2;
          break;
        default:
          newTab = 0;
      }
      setSelectedTab(currentTab => {
        if (newTab !== currentTab) {
          return newTab;
        }
        return currentTab;
      });
    };

    window.addEventListener('hashchange', handleHashChange);

    // Check initial hash on mount
    handleHashChange();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return (
    <ApplicantsContext.Provider value={{ hideProfile, unhideProfile, updateStatus }}>
      <div className="job-subtabs job-subtabs--standalone">
        <EmbeddedTabs defaultIndex={selectedTab} selectedIndex={selectedTab} onTabChange={setSelectedTab}>
          {/* Modern Tab List - Using job-subtabs styling */}
          <EmbeddedTabList className="job-subtabs__nav">
            <EmbeddedTab index={0} className="job-subtabs__tab">
              <i className="fa fa-users"></i>
              <span>My Apps</span>
              <span className="badge-modern badge-modern--sm badge-modern--info">
                {filtered_applicants.length}
              </span>
              {filtered_applicants.filter(applicant => !applicant.seen).length > 0 && (
                <span className="badge-modern badge-modern--sm" style={{
                  background: 'linear-gradient(135deg, #dc3545, #c82333)',
                  color: 'white'
                }}>New</span>
              )}
            </EmbeddedTab>
            <EmbeddedTab index={1} className="job-subtabs__tab">
              <i className="fa fa-graduation-cap"></i>
              <span>Dental Newbies</span>
              <HelpIconWithTooltip message="newbie" style={{ display: 'inline', lineHeight: '4px', margin: 0, padding: 0, fontSize: 14 }} />
              <span className="badge-modern badge-modern--sm badge-modern--info">
                {filtered_less_qualified_applicants.length}
              </span>
              {filtered_less_qualified_applicants.filter(applicant => !applicant.seen).length > 0 && (
                <span className="badge-modern badge-modern--sm" style={{
                  background: 'linear-gradient(135deg, #dc3545, #c82333)',
                  color: 'white'
                }}>New</span>
              )}
            </EmbeddedTab>
            <EmbeddedTab index={2} className="job-subtabs__tab">
              <i className="fa fa-trash"></i>
              <span>Deleted Apps</span>
              <span className="badge-modern badge-modern--sm badge-modern--light">
                {state.hidden_applicants.length}
              </span>
            </EmbeddedTab>
          </EmbeddedTabList>

          {/* Modern Filter Bar */}
          <FilterBar filters={filters} onFiltersChange={setFilters} />

          <div className="job-subtabs__content">
            <EmbeddedTabPanel index={0} className="job-subtabs__panel">
              {filtered_applicants.length < 1 && (js_applicants < 1) && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem 2rem',
                  background: 'white',
                  borderRadius: '0.75rem',
                  border: '1px solid #e8ecef',
                  marginBottom: '1.5rem'
                }}>
                  <i className="fa fa-inbox" style={{
                    fontSize: '3rem',
                    color: '#085ec8',
                    marginBottom: '1rem',
                    display: 'block'
                  }}></i>
                  <h4 style={{
                    color: '#034ba6',
                    fontWeight: 600,
                    marginBottom: '0.5rem'
                  }}>You currently have no candidates.</h4>
                </div>
              )}
              {filtered_applicants.length < 1 && (js_applicants < 1) && essential === true && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem 2rem',
                  background: 'linear-gradient(135deg, rgba(8, 94, 200, 0.05), rgba(23, 111, 201, 0.05))',
                  borderRadius: '0.75rem',
                  border: '1px solid rgba(8, 94, 200, 0.1)'
                }}>
                  <i className="fa fa-search" style={{
                    fontSize: '3rem',
                    color: '#085ec8',
                    marginBottom: '1rem',
                    display: 'block'
                  }}></i>
                  <h4 style={{
                    color: '#034ba6',
                    fontWeight: 600,
                    marginBottom: '1rem'
                  }}>Search candidates to invite to apply and send messages.</h4>
                  <a href="/profiles" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textDecoration: 'none',
                    background: 'linear-gradient(135deg, #085ec8, #176fc9)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(8, 94, 200, 0.3)'
                  }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(8, 94, 200, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 94, 200, 0.3)';
                    }}
                  >
                    <i className="fa fa-search"></i> Search Candidates
                  </a>
                </div>
              )}

              <Applicants
                favorites={favorites}
                applicants={filtered_applicants}
                authenticity_token={authenticity_token}
                notes={state.notes}
                load_notes={getNotes}
                loaded={state.loaded}
                timezone={timezone}
                position_type={position_type}
                job={job}
                requested={requested}
                accepted={accepted}
                declined={declined}
              />

              {/* If filtered_less_qualified_applicants > 0, show a button to view less qualified applicants, switch to tab 1 */}
              {filtered_less_qualified_applicants.length > 0 && (
                <div className="text-center" style={{ marginTop: '2rem' }}>
                  <button
                    onClick={() => setSelectedTab(1)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '0.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      background: 'linear-gradient(135deg, #085ec8, #176fc9)',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(8, 94, 200, 0.3)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(8, 94, 200, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 94, 200, 0.3)';
                    }}
                  >
                    <i className="fa fa-graduation-cap"></i>
                    View {filtered_less_qualified_applicants.length} Dental Newbies
                  </button>
                </div>
              )}

            </EmbeddedTabPanel>
            <EmbeddedTabPanel index={1} className="job-subtabs__panel">
              <div style={{
                background: 'rgba(8, 94, 200, 0.03)',
                border: '1px solid rgba(8, 94, 200, 0.1)',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem',
                color: '#034ba6',
                fontSize: '0.95rem',
                lineHeight: '1.6'
              }}>
                <i className="fa fa-info-circle" style={{ marginRight: '0.5rem' }}></i>
                This is where we keep your less qualified job applicants. These applicants answered "no" to our experience-related screening questions, but may still be a good candidate that is eager to gain experience.
              </div>

              <Applicants
                favorites={favorites}
                applicants={filtered_less_qualified_applicants}
                authenticity_token={authenticity_token}
                notes={state.notes}
                load_notes={getNotes}
                loaded={state.loaded}
                timezone={timezone}
                position_type={position_type}
                job={job}
                requested={requested}
                accepted={accepted}
                declined={declined}
              />

            </EmbeddedTabPanel>
            <EmbeddedTabPanel index={2} className="job-subtabs__panel">
              <div style={{
                background: 'rgba(220, 53, 69, 0.05)',
                border: '1px solid rgba(220, 53, 69, 0.15)',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem',
                color: '#721c24',
                fontSize: '0.95rem'
              }}>
                <i className="fa fa-info-circle" style={{ marginRight: '0.5rem' }}></i>
                Applicants that have been blocked or deleted will appear here.
              </div>
              {state.hidden_applicants.length < 1 && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem 2rem',
                  background: 'white',
                  borderRadius: '0.75rem',
                  border: '1px solid #e8ecef',
                  marginBottom: '1.5rem'
                }}>
                  <i className="fa fa-check-circle" style={{
                    fontSize: '3rem',
                    color: '#28a745',
                    marginBottom: '1rem',
                    display: 'block'
                  }}></i>
                  <h4 style={{
                    color: '#034ba6',
                    fontWeight: 600,
                    marginBottom: '0.5rem'
                  }}>No deleted applications</h4>
                  <p style={{
                    color: '#6c757d',
                    fontSize: '0.95rem'
                  }}>Applications you delete will appear here for recovery.</p>
                </div>
              )}
              <Applicants
                favorites={favorites}
                applicants={state.hidden_applicants}
                authenticity_token={authenticity_token}
                notes={state.notes}
                load_notes={getNotes}
                loaded={state.loaded}
                timezone={timezone}
                position_type={position_type}
                job={job}
                requested={requested}
                accepted={accepted}
                declined={declined}
                isHiddenList={true}
              />
            </EmbeddedTabPanel>
          </div>
        </EmbeddedTabs>
      </div>
    </ApplicantsContext.Provider>
  )
}

export default ManageApplicants;
